<template>
  <BaseModal
    :heading="order ? `${t('global.actions.refund_sale')}: ${order?.number}` : t('global.actions.refund_sale')"
    useForm
    @submit="submit"
    @close="$emit('close')"
    testId="refund-sale"
    :loading="loading"
    grey
  >
    <Flex direction="column">
      <BaseAlert
        v-if="onlinePaymentNotice"
        color="warning"
        :text="t('register.invoice.refund_online_payment_notice', brandName())"
        v-test="'refund-sale-online-payment-notice'"
      />
      <BaseCard
        v-if="order?.customer"
        :heading="filters.capitalize(t('global.items.customer', 1))"
      >
        <Customer
          :orderId="orderId"
          :loading="loading"
          :customer="order?.customer"
          :hasActions="false"
          @updated="order.customer = $event"
          withoutLabel
        />
      </BaseCard>
      <Details :order="order" />
      <RefundMethod :order="order" />
    </Flex>
    <template #footer>
      <BaseButton
        @click="$emit('close')"
        v-test="'refund-sale-cancel'"
        color="inverted"
      >
        {{ t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        submitForm
        :loading="loadingUpdateOrder"
        v-test="'refund-sale-submit'"
        :disabled="disableItemRefund"
      >
        {{ t('global.actions.save') }}
      </BaseButton>
    </template>

    <OpenDrawerModal
      v-if="showOpenDrawerModal"
      @close="showOpenDrawerModal = false"
      @success="submit"
    />
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import { provide, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Customer from '@/components/customer/index.vue';
import filters from '@/filters';
import { brandName } from '@/helpers/brand-name';
import { compliantKey } from '@/helpers/compliant-register';
import { flash } from '@/helpers/ui';
import Details from '@/modals/sale/Details.vue';
import { segment } from '@/segment';
import { useLocationsStore } from '@/stores/locations';
import { useRegisterDrawerStore } from '@/stores/register-drawer';
import type { OrderItem } from '@/types';
import { useRegisterStore } from '../../stores/register';
import { GET_ORDER, UPDATE_ORDER } from '../graphql';
import OpenDrawerModal from '../open-drawer/index.vue';
import RefundMethod from './RefundMethod.vue';
import {
  buildRefundTransactions,
  itemLineTotal,
  refundableItems,
} from './utils';

const { locationId } = useLocationsStore();
const { cashupEnabled } = storeToRefs(useRegisterStore());
const drawerStore = useRegisterDrawerStore();
const { isOpen: isDrawerOpen, isLoaded: isDrawerLoaded } =
  storeToRefs(drawerStore);

// The drawer status is only fetched by the register module, so it is missing
// when this modal opens from a customer or appointment.
const drawerStatusLoaded =
  cashupEnabled.value && !isDrawerLoaded.value
    ? drawerStore.getDrawerStatus()
    : Promise.resolve();

const props = withDefaults(
  defineProps<{
    orderId: string;
    onlinePaymentNotice?: boolean;
  }>(),
  {
    onlinePaymentNotice: false,
  },
);

const emit = defineEmits(['close', 'submit']);

const { t } = useI18n();

const { loading, result } = useQuery(GET_ORDER, {
  id: props.orderId,
});

const order = computed(() => result.value?.order);

const showOpenDrawerModal = ref(false);

const selectedItems = ref<string[]>([]);
provide('selectedItems', selectedItems);

const selectedTotal = ref(0);
provide('selectedTotal', selectedTotal);

const selectedPaymentMethod = ref('');
provide('selectedPaymentMethod', selectedPaymentMethod);

const availableRefundAmount = computed(
  () => order.value?.refundableAmount ?? 0,
);

provide('availableRefundAmount', availableRefundAmount);

const disableItemRefund = computed(() => selectedTotal.value === 0);

const {
  mutate: updateOrder,
  onDone,
  loading: loadingUpdateOrder,
} = useMutation(UPDATE_ORDER, {
  update(cache) {
    cache.evict({ id: 'ROOT_QUERY', fieldName: 'orders' });
    cache.gc();
  },
});

onDone(() => {
  emit('submit');
  flash(t(compliantKey('global.flash.order_updated')));
});

const submit = async () => {
  await drawerStatusLoaded;

  if (cashupEnabled.value && !isDrawerOpen.value) {
    showOpenDrawerModal.value = true;
    return;
  }

  segment.track('register.sales.refund.confirmed', {
    payment_method: selectedPaymentMethod.value,
  });

  const remainingItems = refundableItems(order.value?.items).filter(
    (item: OrderItem) => !selectedItems.value.includes(item.id),
  );
  const remainingItemsSum = remainingItems.reduce(
    (sum: number, item: OrderItem) => sum + itemLineTotal(item),
    0,
  );

  const transactions = buildRefundTransactions({
    remainingItemsSum,
    availableRefundAmount: availableRefundAmount.value,
    selectedPaymentMethod: selectedPaymentMethod.value,
    locationId: Number(locationId),
  });

  const input = {
    id: props.orderId,
    refund: true,
    correction: true,
    items: selectedItems.value.map((id) => ({ id, quantity: 0 })),
    transactions,
  };

  updateOrder({ input });
};
</script>
